import React, { useEffect, useCallback, forwardRef, useRef, useState, useImperativeHandle } from 'react';
import 'jb-input';

function useBindEvent(ref, event, handler, passive = false) {
    useEffect(() => {
        const dom = ref.current;
        if (dom) {
            // initiate the event handler
            dom.addEventListener(event, handler, passive);
        }
        // this will clean up the event every time the component is re-rendered
        return function cleanup() {
            if (dom) {
                dom.removeEventListener(event, handler, passive);
            }
        };
    }, [ref, event, handler, passive]);
}

function useJBInputEvents(element, props) {
    const onChange = useCallback((e) => {
        if (typeof props.onChange === "function") {
            props.onChange(e);
        }
    }, [props.onChange]);
    const onKeydown = useCallback((e) => {
        if (typeof props.onKeydown === "function") {
            props.onKeydown(e);
        }
    }, [props.onKeydown]);
    const onKeyup = useCallback((e) => {
        if (typeof props.onKeyup === "function") {
            props.onKeyup(e);
        }
    }, [props.onKeyup]);
    const onEnter = useCallback((e) => {
        if (props.onEnter) {
            props.onEnter(e);
        }
    }, [props.onEnter]);
    const onFocus = useCallback((e) => {
        if (props.onFocus && e instanceof FocusEvent) {
            props.onFocus(e);
        }
    }, [props.onFocus]);
    const onBlur = useCallback((e) => {
        if (props.onBlur && e instanceof FocusEvent) {
            props.onBlur(e);
        }
    }, [props.onBlur]);
    const onInput = useCallback((e) => {
        if (typeof props.onInput == 'function' && e instanceof InputEvent) {
            props.onInput(e);
        }
    }, [props.onInput]);
    const onBeforeInput = useCallback((e) => {
        if (typeof props.onBeforeinput == 'function' && e instanceof InputEvent) {
            props.onBeforeinput(e);
        }
    }, [props.onBeforeinput]);
    useBindEvent(element, 'change', onChange);
    useBindEvent(element, 'keydown', onKeydown);
    useBindEvent(element, 'keyup', onKeyup);
    useBindEvent(element, 'focus', onFocus);
    useBindEvent(element, 'blur', onBlur);
    useBindEvent(element, 'enter', onEnter);
    useBindEvent(element, 'input', onInput);
    useBindEvent(element, 'beforeinput', onBeforeInput);
}

function useJBInputAttribute(element, props) {
    useEffect(() => {
        let value = props.value;
        if (props.value == null || props.value === undefined) {
            value = '';
        }
        if (element && element.current && element.current) {
            element.current.value = value?.toString() || "";
        }
    }, [props.value]);
    useEffect(() => {
        if (props.type) {
            element?.current?.setAttribute('type', props.type);
        }
    }, [props.type]);
    useEffect(() => {
        element?.current?.setAttribute('name', props.name || '');
    }, [props.name]);
    useEffect(() => {
        if (element && element.current) {
            element.current.validation.list = props.validationList || [];
        }
    }, [props.validationList]);
    useEffect(() => {
        element?.current?.setAttribute('label', props.label || "");
    }, [props.label]);
    useEffect(() => {
        if (typeof props.disabled == "boolean") {
            element?.current?.setAttribute('disabled', `${props.disabled}`);
        }
    }, [props.disabled]);
    useEffect(() => {
        if (typeof props.required == "boolean") {
            element?.current?.setAttribute('required', `${props.required}`);
        }
    }, [props.required]);
    useEffect(() => {
        if (props.inputmode) {
            element.current?.setAttribute('inputmode', props.inputmode);
        }
        else {
            element.current?.removeAttribute('inputmode');
        }
    }, [props.inputmode]);
    useEffect(() => {
        element?.current?.setAttribute('message', props.message || "");
    }, [props.message]);
    useEffect(() => {
        element?.current?.setAttribute('placeholder', props.placeholder || "");
    }, [props.placeholder]);
}

// eslint-disable-next-line react/display-name
const JBInput = forwardRef((props, ref) => {
    const element = useRef(null);
    const [refChangeCount, refChangeCountSetter] = useState(0);
    useImperativeHandle(ref, () => (element ? element.current : {}), [element]);
    //to force rerender for events
    useEffect(() => {
        refChangeCountSetter(refChangeCount + 1);
    }, [element.current]);
    useJBInputEvents(element, props);
    useJBInputAttribute(element, props);
    return (React.createElement("jb-input", { ref: element, class: props.className }, props.children));
});
JBInput.displayName = "JBInput";

export { JBInput, useJBInputAttribute, useJBInputEvents };
//# sourceMappingURL=JBInput.js.map
